import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dataset } from '@/lib/types';
import { PipelineLibrary } from '@/components/PipelineLibrary';
import {
  ArrowsCounterClockwiseIcon,
  BookmarkIcon,
  CalculatorIcon,
  CheckCircleIcon,
  ClockIcon,
  CopyIcon,
  DatabaseIcon,
  EyeIcon,
  FloppyDiskIcon,
  FlowArrowIcon,
  FunnelIcon,
  GearIcon,
  MathOperationsIcon,
  PlayIcon,
  PlusIcon,
  SortAscendingIcon,
  TargetIcon,
  TrashIcon,
  UsersThreeIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export interface TransformationStep {
  id: string;
  type:
    | 'filter'
    | 'sort'
    | 'group'
    | 'aggregate'
    | 'join'
    | 'select'
    | 'transform'
    | 'clean';
  name: string;
  description: string;
  config: Record<string, any>;
  inputDataset?: string;
  outputName?: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  preview?: any[];
  error?: string;
}

export interface Pipeline {
  id: string;
  name: string;
  description: string;
  steps: TransformationStep[];
  createdAt: string;
  lastModified: string;
  status: 'draft' | 'running' | 'completed' | 'error';
}

const transformationTypes = [
  {
    type: 'filter',
    name: 'Filter Rows',
    description: 'Remove rows based on conditions',
    icon: FunnelIcon,
    color: 'bg-blue-100 text-blue-700',
  },
  {
    type: 'sort',
    name: 'Sort Data',
    description: 'Order rows by column values',
    icon: SortAscendingIcon,
    color: 'bg-green-100 text-green-700',
  },
  {
    type: 'group',
    name: 'Group By',
    description: 'Group rows by column values',
    icon: UsersThreeIcon,
    color: 'bg-purple-100 text-purple-700',
  },
  {
    type: 'aggregate',
    name: 'Aggregate',
    description: 'Calculate summary statistics',
    icon: CalculatorIcon,
    color: 'bg-orange-100 text-orange-700',
  },
  {
    type: 'join',
    name: 'Join Datasets',
    description: 'Combine multiple datasets',
    icon: ArrowsCounterClockwiseIcon,
    color: 'bg-red-100 text-red-700',
  },
  {
    type: 'select',
    name: 'Select Columns',
    description: 'Choose specific columns',
    icon: TargetIcon,
    color: 'bg-teal-100 text-teal-700',
  },
  {
    type: 'transform',
    name: 'Transform Values',
    description: 'Modify column values',
    icon: MathOperationsIcon,
    color: 'bg-indigo-100 text-indigo-700',
  },
  {
    type: 'clean',
    name: 'Clean Data',
    description: 'Handle missing values and outliers',
    icon: GearIcon,
    color: 'bg-yellow-100 text-yellow-700',
  },
];

interface DataTransformationPipelineProps {
  datasets: Dataset[];
  onPipelineRun?: (pipeline: Pipeline) => Promise<void>;
  onPipelineSave?: (pipeline: Pipeline) => Promise<void>;
}

export function DataTransformationPipeline({
  datasets,
  onPipelineRun,
  onPipelineSave,
}: Readonly<DataTransformationPipelineProps>) {
  const [currentPipeline, setCurrentPipeline] = useState<Pipeline>({
    id: `pipeline_${Date.now()}`,
    name: 'New Pipeline',
    description: 'Data transformation pipeline',
    steps: [],
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString(),
    status: 'draft',
  });

  const [showStepConfig, setShowStepConfig] = useState(false);
  const [editingStep, setEditingStep] = useState<TransformationStep | null>(
    null
  );
  const [isRunning, setIsRunning] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);

  const stepIdCounter = useRef(0);
  const [showPreview, setShowPreview] = useState(false);

  const handlePipelineLoad = (loadedPipeline: Pipeline) => {
    setCurrentPipeline(loadedPipeline);
    toast.success(`Loaded pipeline: ${loadedPipeline.name}`);
  };

  const handleTemplateSelect = (template: any) => {
    // Convert template to pipeline
    const newPipeline: Pipeline = {
      id: `pipeline_${Date.now()}`,
      name: template.name,
      description: template.description,
      steps: template.steps.map((step: any, index: number) => ({
        ...step,
        id: `step_${stepIdCounter.current + index}`,
        status: 'pending' as const,
      })),
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      status: 'draft',
    };

    stepIdCounter.current += template.steps.length;
    setCurrentPipeline(newPipeline);
    toast.success(`Applied template: ${template.name}`);
  };

  const createNewStep = useCallback((type: string): TransformationStep => {
    const stepType = transformationTypes.find(t => t.type === type);

    return {
      id: `step_${stepIdCounter.current++}`,
      type: type as TransformationStep['type'],
      name: stepType?.name || 'Unknown Step',
      description: stepType?.description || '',
      config: getDefaultConfig(type),
      status: 'pending',
    };
  }, []);

  const getDefaultConfig = (type: string): Record<string, any> => {
    switch (type) {
      case 'filter':
        return { column: '', operator: 'equals', value: '' };
      case 'sort':
        return { columns: [], ascending: true };
      case 'group':
        return { columns: [] };
      case 'aggregate':
        return { groupBy: [], aggregations: [] };
      case 'join':
        return {
          leftDataset: '',
          rightDataset: '',
          joinType: 'inner',
          leftKey: '',
          rightKey: '',
        };
      case 'select':
        return { columns: [] };
      case 'transform':
        return { column: '', operation: 'multiply', value: 1 };
      case 'clean':
        return { operation: 'dropMissing', columns: [] };
      default:
        return {};
    }
  };

  const addStep = (type: string) => {
    const newStep = createNewStep(type);

    setEditingStep(newStep);
    setShowStepConfig(true);
  };

  const saveStep = (step: TransformationStep) => {
    if (
      editingStep &&
      currentPipeline.steps.find(s => s.id === editingStep.id)
    ) {
      // Update existing step
      setCurrentPipeline(prev => ({
        ...prev,
        steps: prev.steps.map(s => (s.id === step.id ? step : s)),
        lastModified: new Date().toISOString(),
      }));
    } else {
      // Add new step
      setCurrentPipeline(prev => ({
        ...prev,
        steps: [...prev.steps, step],
        lastModified: new Date().toISOString(),
      }));
    }
    setShowStepConfig(false);
    setEditingStep(null);
  };

  const removeStep = (stepId: string) => {
    setCurrentPipeline(prev => ({
      ...prev,
      steps: prev.steps.filter(s => s.id !== stepId),
      lastModified: new Date().toISOString(),
    }));
    toast.success('Step removed from pipeline');
  };

  const duplicateStep = (step: TransformationStep) => {
    const duplicatedStep: TransformationStep = {
      ...step,
      id: `step_${stepIdCounter.current++}`,
      name: `${step.name} (Copy)`,
      status: 'pending',
    };

    setCurrentPipeline(prev => ({
      ...prev,
      steps: [...prev.steps, duplicatedStep],
      lastModified: new Date().toISOString(),
    }));
    toast.success('Step duplicated');
  };

  const runPipeline = async () => {
    if (currentPipeline.steps.length === 0) {
      toast.error('Add transformation steps to run the pipeline');

      return;
    }

    setIsRunning(true);
    setCurrentPipeline(prev => ({ ...prev, status: 'running' }));

    try {
      // Simulate pipeline execution
      for (const step of currentPipeline.steps) {
        // Update step status to running
        setCurrentPipeline(prev => ({
          ...prev,
          steps: prev.steps.map(s =>
            s.id === step.id ? { ...s, status: 'running' } : s
          ),
        }));

        // Simulate processing delay
        await new Promise(resolve =>
          setTimeout(resolve, 1000 + Math.random() * 2000)
        );

        // Simulate some steps failing occasionally
        const shouldFail = Math.random() < 0.1;

        if (shouldFail) {
          setCurrentPipeline(prev => ({
            ...prev,
            steps: prev.steps.map(s =>
              s.id === step.id
                ? {
                    ...s,
                    status: 'error',
                    error:
                      'Transformation failed: Invalid configuration or data type mismatch',
                  }
                : s
            ),
            status: 'error',
          }));
          throw new Error(`Step "${step.name}" failed`);
        }

        // Mark step as completed with preview data
        const mockPreview = generateMockPreview(step.type);

        setCurrentPipeline(prev => ({
          ...prev,
          steps: prev.steps.map(s =>
            s.id === step.id
              ? {
                  ...s,
                  status: 'completed',
                  preview: mockPreview,
                }
              : s
          ),
        }));
      }

      setCurrentPipeline(prev => ({ ...prev, status: 'completed' }));
      toast.success('Pipeline executed successfully!');

      if (onPipelineRun) {
        await onPipelineRun(currentPipeline);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      toast.error(`Pipeline execution failed: ${errorMessage}`);
    } finally {
      setIsRunning(false);
    }
  };

  const generateMockPreview = (stepType: string): any[] => {
    // Generate mock preview data based on step type
    switch (stepType) {
      case 'filter':
        return [
          { id: 1, name: 'Alice', age: 25, score: 85 },
          { id: 2, name: 'Bob', age: 30, score: 92 },
          { id: 3, name: 'Carol', age: 28, score: 78 },
        ];
      case 'aggregate':
        return [
          { category: 'A', count: 15, avg_score: 84.2 },
          { category: 'B', count: 23, avg_score: 91.7 },
          { category: 'C', count: 8, avg_score: 76.3 },
        ];
      default:
        return [
          { processed: true, timestamp: new Date().toISOString() },
          { processed: true, timestamp: new Date().toISOString() },
        ];
    }
  };

  const savePipeline = async () => {
    try {
      if (onPipelineSave) {
        await onPipelineSave(currentPipeline);
      }
      toast.success('Pipeline saved successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save pipeline';

      toast.error(errorMessage);
    }
  };

  const getStepIcon = (type: string) => {
    const stepType = transformationTypes.find(t => t.type === type);

    return stepType?.icon || GearIcon;
  };

  const getStepColor = (type: string) => {
    const stepType = transformationTypes.find(t => t.type === type);

    return stepType?.color || 'bg-gray-100 text-gray-700';
  };

  const getStatusIcon = (status: TransformationStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className='w-4 h-4 text-green-600' />;
      case 'running':
        return <ClockIcon className='w-4 h-4 text-blue-600 animate-spin' />;
      case 'error':
        return <WarningIcon className='w-4 h-4 text-red-600' />;
      default:
        return <ClockIcon className='w-4 h-4 text-gray-400' />;
    }
  };

  const completedSteps = currentPipeline.steps.filter(
    step => step.status === 'completed'
  ).length;
  const progressPercentage =
    currentPipeline.steps.length > 0
      ? (completedSteps / currentPipeline.steps.length) * 100
      : 0;

  const getPipelineStatusVariant = (status: string) => {
    if (status === 'completed') {
      return 'default';
    }
    if (status === 'running') {
      return 'secondary';
    }
    if (status === 'error') {
      return 'destructive';
    }

    return 'outline';
  };

  const getStepCardStyles = (status: TransformationStep['status']) => {
    if (status === 'error') {
      return 'border-red-200 bg-red-50/50';
    }
    if (status === 'completed') {
      return 'border-green-200 bg-green-50/50';
    }
    if (status === 'running') {
      return 'border-blue-200 bg-blue-50/50';
    }

    return '';
  };

  return (
    <div className='space-y-6'>
      {/* Tabbed Interface */}
      <Tabs defaultValue='builder' className='w-full'>
        <TabsList className='grid w-full grid-cols-2'>
          <TabsTrigger value='library' className='gap-2'>
            <BookmarkIcon className='w-4 h-4' />
            Template Library
          </TabsTrigger>
          <TabsTrigger value='builder' className='gap-2'>
            <FlowArrowIcon className='w-4 h-4' />
            Pipeline Builder
          </TabsTrigger>
        </TabsList>

        {/* Template Library Tab */}
        <TabsContent value='library' className='space-y-6'>
          <PipelineLibrary
            onTemplateSelect={handleTemplateSelect}
            onPipelineLoad={handlePipelineLoad}
          />
        </TabsContent>

        {/* Pipeline Builder Tab */}
        <TabsContent value='builder' className='space-y-6'>
          {/* Pipeline Header */}
          <Card>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div>
                  <CardTitle className='flex items-center gap-2'>
                    <FlowArrowIcon className='w-5 h-5' />
                    Data Transformation Pipeline
                  </CardTitle>
                  <CardDescription>
                    Build interactive data transformation workflows with visual
                    pipeline builder
                  </CardDescription>
                </div>
                <div className='flex items-center gap-2'>
                  <Button
                    onClick={savePipeline}
                    variant='outline'
                    size='sm'
                    className='gap-2'
                  >
                    <FloppyDiskIcon className='w-4 h-4' />
                    Save Pipeline
                  </Button>
                  <Button
                    onClick={runPipeline}
                    disabled={isRunning || currentPipeline.steps.length === 0}
                    className='gap-2'
                  >
                    <PlayIcon className='w-4 h-4' />
                    {isRunning ? 'Running...' : 'Run Pipeline'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                {/* Pipeline Info */}
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <Label htmlFor='pipeline-name'>Pipeline Name</Label>
                    <Input
                      id='pipeline-name'
                      value={currentPipeline.name}
                      onChange={e =>
                        setCurrentPipeline(prev => ({
                          ...prev,
                          name: e.target.value,
                          lastModified: new Date().toISOString(),
                        }))
                      }
                      placeholder='Enter pipeline name'
                    />
                  </div>
                  <div className='md:col-span-2'>
                    <Label htmlFor='pipeline-description'>Description</Label>
                    <Input
                      id='pipeline-description'
                      value={currentPipeline.description}
                      onChange={e =>
                        setCurrentPipeline(prev => ({
                          ...prev,
                          description: e.target.value,
                          lastModified: new Date().toISOString(),
                        }))
                      }
                      placeholder='Describe what this pipeline does'
                    />
                  </div>
                </div>

                {/* Progress Bar */}
                {currentPipeline.steps.length > 0 && (
                  <div className='space-y-2'>
                    <div className='flex items-center justify-between text-sm'>
                      <span>Pipeline Progress</span>
                      <span>
                        {completedSteps}/{currentPipeline.steps.length} steps
                        completed
                      </span>
                    </div>
                    <Progress value={progressPercentage} className='h-2' />
                  </div>
                )}

                {/* Pipeline Status */}
                <div className='flex items-center gap-4'>
                  <Badge
                    variant={getPipelineStatusVariant(currentPipeline.status)}
                  >
                    {currentPipeline.status}
                  </Badge>
                  <span className='text-sm text-muted-foreground'>
                    {currentPipeline.steps.length} step
                    {currentPipeline.steps.length !== 1 ? 's' : ''}
                  </span>
                  <span className='text-sm text-muted-foreground'>
                    Last modified:{' '}
                    {new Date(currentPipeline.lastModified).toLocaleString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Datasets */}
          {datasets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <DatabaseIcon className='w-5 h-5' />
                  Available Datasets
                </CardTitle>
                <CardDescription>
                  {datasets.length} dataset{datasets.length !== 1 ? 's' : ''}{' '}
                  available for transformation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {datasets.map(dataset => (
                    <div
                      key={dataset.id}
                      className='flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors'
                    >
                      <div className='p-2 rounded bg-primary/10'>
                        <DatabaseIcon className='w-4 h-4 text-primary' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='font-medium text-sm truncate'>
                          {dataset.name}
                        </div>
                        <div className='text-xs text-muted-foreground'>
                          {dataset.totalRows.toLocaleString()} rows,{' '}
                          {dataset.schema.length} columns
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pipeline Builder */}
          <div className='grid grid-cols-1 lg:grid-cols-4 gap-6'>
            {/* Transformation Types */}
            <Card className='lg:col-span-1'>
              <CardHeader>
                <CardTitle className='text-lg'>Transformation Steps</CardTitle>
                <CardDescription>
                  Drag or click to add transformation steps
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='space-y-2'>
                  {transformationTypes.map(type => {
                    const Icon = type.icon;

                    return (
                      <Button
                        key={type.type}
                        variant='outline'
                        className='w-full justify-start gap-3 h-auto p-3'
                        onClick={() => addStep(type.type)}
                      >
                        <div className={`p-1.5 rounded ${type.color}`}>
                          <Icon className='w-4 h-4' />
                        </div>
                        <div className='text-left flex-1'>
                          <div className='font-medium text-sm'>{type.name}</div>
                          <div className='text-xs text-muted-foreground'>
                            {type.description}
                          </div>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Pipeline Steps */}
            <Card className='lg:col-span-3'>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <FlowArrowIcon className='w-5 h-5' />
                  Pipeline Steps
                </CardTitle>
                <CardDescription>
                  Configure your data transformation workflow
                </CardDescription>
              </CardHeader>
              <CardContent>
                {currentPipeline.steps.length === 0 ? (
                  <div className='text-center py-12 border-2 border-dashed border-muted rounded-lg'>
                    <FlowArrowIcon className='w-12 h-12 mx-auto mb-4 text-muted-foreground' />
                    <h3 className='text-lg font-semibold mb-2'>
                      Start Building Your Pipeline
                    </h3>
                    <p className='text-muted-foreground mb-4'>
                      Add transformation steps from the left panel to create
                      your data processing workflow
                    </p>
                    <Button onClick={() => addStep('filter')} className='gap-2'>
                      <PlusIcon className='w-4 h-4' />
                      Add First Step
                    </Button>
                  </div>
                ) : (
                  <div className='space-y-4'>
                    {currentPipeline.steps.map((step, index) => {
                      const Icon = getStepIcon(step.type);

                      return (
                        <div key={step.id} className='relative'>
                          {/* Connection Line */}
                          {index < currentPipeline.steps.length - 1 && (
                            <div className='absolute left-6 top-16 w-0.5 h-8 bg-border z-0' />
                          )}

                          {/* Step Card */}
                          <Card
                            className={`relative z-10 ${getStepCardStyles(step.status)}`}
                          >
                            <CardContent className='p-4'>
                              <div className='flex items-center gap-4'>
                                {/* Step Icon */}
                                <div
                                  className={`p-2 rounded-lg ${getStepColor(step.type)} flex-shrink-0`}
                                >
                                  <Icon className='w-5 h-5' />
                                </div>

                                {/* Step Info */}
                                <div className='flex-1 min-w-0'>
                                  <div className='flex items-center gap-2 mb-1'>
                                    <h4 className='font-medium truncate'>
                                      {step.name}
                                    </h4>
                                    {getStatusIcon(step.status)}
                                    <Badge
                                      variant='outline'
                                      className='text-xs'
                                    >
                                      {step.type}
                                    </Badge>
                                  </div>
                                  <p className='text-sm text-muted-foreground mb-2'>
                                    {step.description}
                                  </p>

                                  {/* Step Configuration Preview */}
                                  <div className='text-xs text-muted-foreground'>
                                    {Object.entries(step.config)
                                      .slice(0, 2)
                                      .map(([key, value]) => (
                                        <span key={key} className='mr-3'>
                                          {key}: {String(value).slice(0, 20)}
                                          {String(value).length > 20
                                            ? '...'
                                            : ''}
                                        </span>
                                      ))}
                                  </div>

                                  {/* Error Message */}
                                  {step.error && (
                                    <Alert className='mt-2'>
                                      <WarningIcon className='h-4 w-4' />
                                      <AlertTitle>Step Failed</AlertTitle>
                                      <AlertDescription className='text-xs'>
                                        {step.error}
                                      </AlertDescription>
                                    </Alert>
                                  )}
                                </div>

                                {/* Step Actions */}
                                <div className='flex items-center gap-1 flex-shrink-0'>
                                  {step.preview && (
                                    <Button
                                      variant='ghost'
                                      size='sm'
                                      onClick={() => {
                                        setPreviewData(step.preview || []);
                                        setShowPreview(true);
                                      }}
                                    >
                                      <EyeIcon className='w-4 h-4' />
                                    </Button>
                                  )}
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    onClick={() => {
                                      setEditingStep(step);
                                      setShowStepConfig(true);
                                    }}
                                  >
                                    <GearIcon className='w-4 h-4' />
                                  </Button>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    onClick={() => duplicateStep(step)}
                                  >
                                    <CopyIcon className='w-4 h-4' />
                                  </Button>
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    onClick={() => removeStep(step.id)}
                                  >
                                    <TrashIcon className='w-4 h-4' />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}

                    {/* Add Step Button */}
                    <div className='text-center pt-4'>
                      <Button
                        variant='outline'
                        onClick={() => {
                          setEditingStep(null);
                          setShowStepConfig(true);
                        }}
                        className='gap-2'
                      >
                        <PlusIcon className='w-4 h-4' />
                        Add Step
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Step Configuration Dialog */}
      <StepConfigurationDialog
        isOpen={showStepConfig}
        onClose={() => {
          setShowStepConfig(false);
          setEditingStep(null);
        }}
        step={editingStep}
        datasets={datasets}
        onSave={saveStep}
        transformationTypes={transformationTypes}
      />

      {/* Preview Data Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className='max-w-4xl max-h-[80vh]'>
          <DialogHeader>
            <DialogTitle>Step Output Preview</DialogTitle>
            <DialogDescription>
              Preview of transformed data from the selected step
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className='h-96'>
            {previewData.length > 0 ? (
              <div className='space-y-2'>
                <div className='text-sm text-muted-foreground mb-2'>
                  Showing {previewData.length} rows
                </div>
                <pre className='text-xs bg-muted p-4 rounded overflow-auto'>
                  {JSON.stringify(previewData, null, 2)}
                </pre>
              </div>
            ) : (
              <div className='text-center py-8 text-muted-foreground'>
                No preview data available
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Step Configuration Dialog Component
interface StepConfigurationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  step: TransformationStep | null;
  datasets: Dataset[];
  onSave: (step: TransformationStep) => void;
  transformationTypes: any[];
}

function StepConfigurationDialog({
  isOpen,
  onClose,
  step,
  datasets,
  onSave,
  transformationTypes,
}: Readonly<StepConfigurationDialogProps>) {
  const [editedStep, setEditedStep] = useState<TransformationStep | null>(null);

  // Initialize editedStep when step changes
  useEffect(() => {
    if (step) {
      setEditedStep({ ...step });
    }
  }, [step]);

  const handleSave = () => {
    if (editedStep) {
      onSave(editedStep);
    }
  };

  const updateConfig = (key: string, value: any) => {
    if (!editedStep) {
      return;
    }
    setEditedStep(prev =>
      prev
        ? {
            ...prev,
            config: {
              ...prev.config,
              [key]: value,
            },
          }
        : null
    );
  };

  const renderConfigFields = () => {
    if (!editedStep) {
      return null;
    }

    switch (editedStep.type) {
      case 'filter':
        return (
          <div className='space-y-4'>
            <div>
              <Label>Column</Label>
              <Select
                value={editedStep.config.column}
                onValueChange={value => updateConfig('column', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Select column' />
                </SelectTrigger>
                <SelectContent>
                  {datasets
                    .flatMap(d => d.schema)
                    .map(field => (
                      <SelectItem key={field.name} value={field.name}>
                        {field.name} ({field.type})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Operator</Label>
              <Select
                value={editedStep.config.operator}
                onValueChange={value => updateConfig('operator', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='equals'>Equals</SelectItem>
                  <SelectItem value='not_equals'>Not Equals</SelectItem>
                  <SelectItem value='greater_than'>Greater Than</SelectItem>
                  <SelectItem value='less_than'>Less Than</SelectItem>
                  <SelectItem value='contains'>Contains</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input
                value={editedStep.config.value}
                onChange={e => updateConfig('value', e.target.value)}
                placeholder='Filter value'
              />
            </div>
          </div>
        );

      case 'sort':
        return (
          <div className='space-y-4'>
            <div>
              <Label>Sort Columns</Label>
              <Textarea
                value={editedStep.config.columns.join(', ')}
                onChange={e =>
                  updateConfig(
                    'columns',
                    e.target.value.split(',').map(c => c.trim())
                  )
                }
                placeholder='Enter column names separated by commas'
              />
            </div>
            <div className='flex items-center space-x-2'>
              <input
                type='checkbox'
                checked={editedStep.config.ascending}
                onChange={e => updateConfig('ascending', e.target.checked)}
              />
              <Label>Ascending Order</Label>
            </div>
          </div>
        );

      default:
        return (
          <div className='space-y-4'>
            <div>
              <Label>Configuration</Label>
              <Textarea
                value={JSON.stringify(editedStep.config, null, 2)}
                onChange={e => {
                  try {
                    const config = JSON.parse(e.target.value);

                    setEditedStep(prev => (prev ? { ...prev, config } : null));
                  } catch {
                    // Invalid JSON, keep previous state
                  }
                }}
                className='font-mono text-sm'
                rows={6}
              />
              <p className='text-xs text-muted-foreground mt-1'>
                Configure step parameters in JSON format
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-2xl'>
        <DialogHeader>
          <DialogTitle>
            {step ? 'Configure Step' : 'Add Transformation Step'}
          </DialogTitle>
          <DialogDescription>
            Set up the parameters for your transformation step
          </DialogDescription>
        </DialogHeader>

        {editedStep && (
          <div className='space-y-6'>
            {/* Basic Info */}
            <div className='space-y-4'>
              <div>
                <Label htmlFor='step-name'>Step Name</Label>
                <Input
                  id='step-name'
                  value={editedStep.name}
                  onChange={e =>
                    setEditedStep(prev =>
                      prev ? { ...prev, name: e.target.value } : null
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor='step-description'>Description</Label>
                <Input
                  id='step-description'
                  value={editedStep.description}
                  onChange={e =>
                    setEditedStep(prev =>
                      prev ? { ...prev, description: e.target.value } : null
                    )
                  }
                />
              </div>
            </div>

            <Separator />

            {/* Configuration Fields */}
            <div>
              <h4 className='font-medium mb-4'>Step Configuration</h4>
              {renderConfigFields()}
            </div>

            <div className='flex justify-end gap-2'>
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Step</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
